// Super_admin-only detail view for one Publica push job. Renders each
// worker step's status + error, and offers a "Retry job" action that flips
// the row back to pending so the worker picks it up on its next poll.

import React from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, ExternalLink, Loader2, RotateCcw } from 'lucide-react';
import { useAuth } from '../../components/self-service/auth/useAuth';
import { apiClient, ApiError } from '../../lib/apiClient';
import * as analytics from '../../lib/analytics';
import { EVENTS } from '../../lib/analytics';
import { formatDateTime } from '../../components/self-service/shared/dateFormat';
import { statusPillClass } from './publicaPushJobHelpers';

type StepStatus = 'pending' | 'processing' | 'completed' | 'failed' | null;
type JobStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'partial';

interface LineItemError {
  flight_id?: string;
  error?: string;
}

interface PushJobDetail {
  id: string;
  selfservice_submission_id: string;
  organization_id: string | null;
  station_id: string | null;
  station_slug: string | null;
  station_name: string | null;
  advertiser_name: string | null;
  campaign_name: string | null;
  job_kind: 'create' | 'edit' | 'creative_swap' | 'refund_cleanup';
  parent_job_id: string | null;
  job_id: string | null;
  status: JobStatus;

  advertiser_status: StepStatus;
  advertiser_id: string | null;
  advertiser_error: string | null;

  order_status: StepStatus;
  order_id: string | null;
  order_error: string | null;

  line_items_status: StepStatus;
  line_item_ids: string[] | null;
  line_items_total: number | null;
  line_items_completed: number | null;
  line_items_errors: LineItemError[] | null;

  creatives_status: StepStatus;
  creative_ids: string[] | null;
  creatives_errors: unknown;

  bidder_status: StepStatus;
  bidder_id: string | null;
  bidder_checkback_token: string | null;
  bidder_error: string | null;

  retry_count: number;
  max_retries: number;
  last_error: string | null;

  payload: unknown;

  created_at: string;
  updated_at: string;
  started_at: string | null;
  completed_at: string | null;

  failed_step: string | null;
}

const stepPillClass = (status: StepStatus) => {
  switch (status) {
    case 'failed':    return 'bg-red-100 text-red-700 border border-red-200';
    case 'processing':return 'bg-blue-100 text-blue-700 border border-blue-200';
    case 'pending':   return 'bg-slate-100 text-slate-700 border border-slate-200';
    case 'completed': return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
    default:          return 'bg-slate-50 text-slate-500 border border-slate-200';
  }
};

const StepCard: React.FC<{ title: string; status: StepStatus; children: React.ReactNode }> = ({
  title,
  status,
  children,
}) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4">
    <div className="flex items-center gap-2 mb-3">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${stepPillClass(status)}`}>
        {status ?? '—'}
      </span>
    </div>
    <div className="text-sm text-slate-700 space-y-2">{children}</div>
  </div>
);

const Mono: React.FC<{ value: string | null | undefined }> = ({ value }) =>
  value ? <span className="font-mono text-xs text-slate-700">{value}</span> : <span className="text-slate-400">—</span>;

const ErrorPanel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <pre className="whitespace-pre-wrap text-xs bg-red-50 text-red-700 border border-red-200 rounded p-2">{children}</pre>
);

const PublisherPublicaPushJobDetailPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();

  const [job, setJob] = React.useState<PushJobDetail | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [retrying, setRetrying] = React.useState(false);
  const [showPayload, setShowPayload] = React.useState(false);

  const refresh = React.useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.post<{ job: PushJobDetail }>(`/publica-push-jobs/${jobId}`);
      setJob(data.job);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed to load job';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  React.useEffect(() => { refresh(); }, [refresh]);

  if (!isAuthenticated || !user) {
    return (
      <Navigate
        to="/admin"
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }
  if (user.rawRole !== 'super_admin') {
    return <Navigate to="/admin" replace />;
  }
  if (!jobId) return <Navigate to="/admin/publica-push-jobs" replace />;

  const retryable = job?.status === 'failed' || job?.status === 'partial';

  const handleRetry = async () => {
    if (!retryable) return;
    if (!window.confirm('Retry this job? The worker will pick it up within ~30 seconds.')) return;
    setRetrying(true);
    try {
      const data = await apiClient.post<{ job: PushJobDetail }>(`/publica-push-jobs/${jobId}/retry`);
      analytics.track(EVENTS.PUBLICA_PUSH_RETRIED, { station_slug: job?.station_slug ?? null, job_id: jobId });
      setJob(data.job);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Retry failed';
      setError(msg);
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-8 py-8 md:py-12">
        <Link
          to="/admin/publica-push-jobs"
          className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Back to push jobs
        </Link>

        {loading && !job ? (
          <div className="text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…
          </div>
        ) : error && !job ? (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</p>
        ) : !job ? null : (
          <>
            <div className="mb-6">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  {job.campaign_name || job.advertiser_name || 'Push job'}
                </h1>
                <span className={`text-xs font-medium px-2 py-0.5 rounded ${statusPillClass(job.status)}`}>
                  {job.status}
                </span>
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                  {job.job_kind}
                </span>
              </div>
              <div className="text-xs text-slate-500 flex gap-3 flex-wrap">
                <span>Job id: <Mono value={job.id} /></span>
                <span>Station: {job.station_slug ?? '—'}</span>
                <span>Created {formatDateTime(job.created_at)}</span>
                <span>Updated {formatDateTime(job.updated_at)}</span>
              </div>
              <div className="mt-2">
                <Link
                  to={`/admin/${job.station_slug}/submissions/${job.selfservice_submission_id}`}
                  className="inline-flex items-center gap-1 text-sm text-slate-700 hover:text-slate-900 underline"
                >
                  View submission <ExternalLink className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>

            {error && <p className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</p>}

            <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-semibold text-slate-900">Retry</h2>
                <span className="text-xs text-slate-500">
                  Attempts {job.retry_count} / {job.max_retries}
                </span>
              </div>
              {job.last_error && (
                <div className="mb-3">
                  <div className="text-xs text-slate-500 mb-1">Last error</div>
                  <ErrorPanel>{job.last_error}</ErrorPanel>
                </div>
              )}
              <button
                type="button"
                onClick={handleRetry}
                disabled={!retryable || retrying}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                Retry job
              </button>
              {!retryable && (
                <p className="text-xs text-slate-500 mt-2">
                  Only jobs in <code>failed</code> or <code>partial</code> state can be retried.
                </p>
              )}
            </div>

            <div className="space-y-3">
              <StepCard title="1. Advertiser" status={job.advertiser_status}>
                <div className="flex gap-3 flex-wrap">
                  <span className="text-xs text-slate-500">Publica advertiser id:</span>
                  <Mono value={job.advertiser_id} />
                </div>
                {job.advertiser_error && <ErrorPanel>{job.advertiser_error}</ErrorPanel>}
              </StepCard>

              <StepCard title="2. Order" status={job.order_status}>
                <div className="flex gap-3 flex-wrap">
                  <span className="text-xs text-slate-500">Publica order id:</span>
                  <Mono value={job.order_id} />
                </div>
                {job.order_error && <ErrorPanel>{job.order_error}</ErrorPanel>}
              </StepCard>

              <StepCard title="3. Line items" status={job.line_items_status}>
                <div className="text-xs text-slate-500">
                  Completed {job.line_items_completed ?? 0} of {job.line_items_total ?? 0}
                </div>
                {Array.isArray(job.line_items_errors) && job.line_items_errors.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs border border-red-200 rounded">
                      <thead className="bg-red-50 text-red-700">
                        <tr>
                          <th className="text-left px-2 py-1 font-medium">Flight id</th>
                          <th className="text-left px-2 py-1 font-medium">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {job.line_items_errors.map((e, i) => (
                          <tr key={i} className="border-t border-red-100">
                            <td className="px-2 py-1 font-mono">{e.flight_id ?? '—'}</td>
                            <td className="px-2 py-1 text-red-700">{e.error ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {job.line_item_ids && job.line_item_ids.length > 0 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-slate-600 hover:text-slate-900">
                      {job.line_item_ids.length} line item id{job.line_item_ids.length === 1 ? '' : 's'}
                    </summary>
                    <ul className="mt-2 space-y-0.5 font-mono">
                      {job.line_item_ids.map((id) => <li key={id}>{id}</li>)}
                    </ul>
                  </details>
                )}
              </StepCard>

              <StepCard title="4. Creatives" status={job.creatives_status}>
                {job.creative_ids && job.creative_ids.length > 0 ? (
                  <details className="text-xs">
                    <summary className="cursor-pointer text-slate-600 hover:text-slate-900">
                      {job.creative_ids.length} creative id{job.creative_ids.length === 1 ? '' : 's'}
                    </summary>
                    <ul className="mt-2 space-y-0.5 font-mono">
                      {job.creative_ids.map((id) => <li key={id}>{id}</li>)}
                    </ul>
                  </details>
                ) : (
                  <span className="text-slate-400 text-xs">No creative ids yet</span>
                )}
                {job.creatives_errors != null && (
                  Array.isArray(job.creatives_errors) ? job.creatives_errors.length > 0 : true
                ) && (
                  <ErrorPanel>{JSON.stringify(job.creatives_errors, null, 2)}</ErrorPanel>
                )}
              </StepCard>

              <StepCard title="5. Bidder" status={job.bidder_status}>
                <div className="flex gap-3 flex-wrap">
                  <span className="text-xs text-slate-500">Bidder id:</span>
                  <Mono value={job.bidder_id} />
                </div>
                <div className="flex gap-3 flex-wrap">
                  <span className="text-xs text-slate-500">Checkback token:</span>
                  <Mono value={job.bidder_checkback_token} />
                </div>
                {job.bidder_error && <ErrorPanel>{job.bidder_error}</ErrorPanel>}
              </StepCard>
            </div>

            <details
              className="mt-6 bg-white border border-slate-200 rounded-xl p-4"
              open={showPayload}
              onToggle={(e) => setShowPayload((e.target as HTMLDetailsElement).open)}
            >
              <summary className="cursor-pointer text-sm font-semibold text-slate-900">
                Payload (worker snapshot)
              </summary>
              <pre className="mt-3 text-xs bg-slate-900 text-slate-100 rounded p-3 overflow-x-auto">
                {JSON.stringify(job.payload, null, 2)}
              </pre>
            </details>
          </>
        )}
      </div>
    </div>
  );
};

export default PublisherPublicaPushJobDetailPage;
